import { useEffect, useState, type ReactNode } from 'react';
import { useConnection } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import Modal from '@/shared/ui/Modal';
import Button from '@/shared/ui/Button';
import { showNotification } from '@/modules/show-notification';
import { isSameAddress } from '@/modules/is-same-address';
import { track } from './track';

// Click-time gate for wallet-bound write affordances (stake, swap): the
// transaction is sent from the CONNECTED wallet, and wagmi is a bystander
// the auth layer ignores — it may be absent or hold a DIFFERENT address
// than the profile's, so an ungated click would spend from the wrong
// wallet under the profile's identity. A null profileAddress is a
// walletless account: no way to attach an address exists client-side (the
// backend exposes no bind endpoint), so that block is terminal — a
// notification, nothing to offer. Any other block opens the connect modal
// instead (consumers render walletGuardModal alongside the guarded
// affordances, which stay visible so the block is explained rather than a
// dead button). The modal renders off the live connection, not the guard's
// click-time snapshot; RainbowKit's connect modal is unavailable while any
// wallet is connected — only a wallet-side switch can fix a mismatch.
export function useWalletActionGuard(profileAddress: string | null): {
  guardWalletAction: () => boolean;
  walletGuardModal: ReactNode;
} {
  const connection = useConnection();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const connectedAddress =
    connection.status === 'connected' ? connection.address : undefined;
  return {
    guardWalletAction: () => {
      if (profileAddress == null) {
        track('wallet_action_blocked', { reason: 'no_wallet' });
        showNotification('error', 'No wallet is linked to this account');
        return false;
      }
      if (
        connectedAddress == null ||
        !isSameAddress(connectedAddress, profileAddress)
      ) {
        track('wallet_action_blocked', {
          reason: connectedAddress == null ? 'not_connected' : 'wrong_wallet',
        });
        setIsModalOpen(true);
        return false;
      }
      return true;
    },
    walletGuardModal:
      isModalOpen && profileAddress != null ?
        <ConnectThisWalletModal
          profileAddress={profileAddress}
          onClose={() => setIsModalOpen(false)}
        />
      : null,
  };
}

function ConnectThisWalletModal({
  profileAddress,
  onClose,
}: {
  profileAddress: string;
  onClose: () => void;
}) {
  const connection = useConnection();
  const { connectModalOpen, openConnectModal } = useConnectModal();
  const connectedAddress =
    connection.status === 'connected' ? connection.address : undefined;
  const isMatching =
    connectedAddress != null && isSameAddress(connectedAddress, profileAddress);
  useEffect(() => {
    if (isMatching) onClose();
  }, [isMatching, onClose]);
  return (
    <Modal
      // RainbowKit's connect modal opens over this one and closes on its own
      // document-level Escape listener, which the same key press also reaches
      // here — this modal must stay open behind it.
      closeModal={() => {
        if (!connectModalOpen) onClose();
      }}
      sheet
    >
      <div className="flex w-125 max-w-full flex-col gap-l px-2xl pt-2xl pb-2xl max-md:w-full max-md:pb-4xl">
        <p className="text-h1 font-medium text-gray-text">
          Connect this wallet
        </p>
        <p className="text-body-lg font-medium text-gray">
          Transactions are sent from the wallet linked to your account:
        </p>
        <p className="rounded-sm bg-background-main px-l py-m font-mono text-body-md font-medium break-all text-gray-text">
          {profileAddress}
        </p>
        {connectedAddress == null ?
          <Button
            size="lg"
            className="w-full"
            onClick={() => openConnectModal?.()}
          >
            Connect wallet
          </Button>
        : <p className="text-body-lg font-medium text-gray-text">
            Your wallet is connected as{' '}
            {`${connectedAddress.slice(0, 6)}…${connectedAddress.slice(-4)}`}.
            Switch to the wallet above in your wallet app — the change is picked
            up automatically.
          </p>
        }
      </div>
    </Modal>
  );
}
